"use client";

import { useVirtualizer } from "@tanstack/react-virtual";
import type { Virtualizer, VirtualItem } from "@tanstack/react-virtual";

/**
 * Row count above which TanStack Virtual is engaged. At or below this
 * threshold the table renders every row directly — virtualization adds a
 * non-trivial layout overhead that hurts more than it helps for small
 * datasets, and a static `<tbody>` is easier to inspect in DevTools.
 *
 * Tuned to 200 because that's the size at which the cost of mounting all
 * rows starts to show up as a perceivable scroll-jank on mid-range laptops.
 */
export const VIRTUALIZE_THRESHOLD = 200;

/**
 * Minimal subset of the TanStack Virtual options we forward. The full
 * options surface is large; the table engine only needs these fields, and
 * pinning the contract here keeps callers from accidentally relying on
 * options the engine isn't ready to thread through yet.
 */
export interface UseVirtualRowsOptions {
  /** Total number of logical rows in the dataset. */
  count: number;
  /** Pixel height of row at the given index (or a constant). */
  estimateSize: (index: number) => number;
  /** Returns the scrollable parent element (or null until mounted). */
  getScrollElement: () => HTMLElement | null;
  /** Number of rows to render outside the viewport on each side. */
  overscan?: number;
  /**
   * Optional initial rect — useful for tests where jsdom has no layout
   * engine and `getBoundingClientRect()` returns zero on first paint.
   */
  initialRect?: { width: number; height: number };
}

/**
 * Return shape of {@link useVirtualRows}.
 *
 * When `count <= VIRTUALIZE_THRESHOLD`:
 *   - `virtualizer` is `null` — caller should render rows directly.
 *   - `virtualRows` is `[]`.
 *   - `totalSize` is `0`.
 *
 * When virtualization is active, `virtualRows` lists only the rows
 * currently in (or near) the viewport, and `totalSize` is the full
 * scrollable height (in px).
 */
export interface UseVirtualRowsResult {
  virtualizer: Virtualizer<HTMLElement, Element> | null;
  virtualRows: VirtualItem[];
  totalSize: number;
}

/**
 * Adapter over `useVirtualizer` that gates activation on row count.
 *
 * The hook always calls `useVirtualizer` (React's rules of hooks forbid
 * conditional hook calls), but ignores its output below the threshold so
 * downstream consumers see a `null` virtualizer signal — the same shape
 * they'd get if the hook had bailed out early.
 */
export function useVirtualRows(options: UseVirtualRowsOptions): UseVirtualRowsResult {
  const { count, estimateSize, getScrollElement, overscan, initialRect } = options;

  const virtualizer = useVirtualizer({
    count,
    estimateSize,
    getScrollElement,
    overscan: overscan ?? 8,
    ...(initialRect !== undefined ? { initialRect } : {}),
  });

  if (count <= VIRTUALIZE_THRESHOLD) {
    return { virtualizer: null, virtualRows: [], totalSize: 0 };
  }

  return {
    virtualizer,
    virtualRows: virtualizer.getVirtualItems(),
    totalSize: virtualizer.getTotalSize(),
  };
}
